import { Component, computed, inject, input } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { OcpTableData } from 'shared';

import { SortableHeaderComponent } from '../../atoms/sortable-header/sortable-header.component';
import { getResolvedColumnWidth, TableColumnConfig } from './table-header.interface';
import { TableColumnResizeHandleComponent } from './table-column-resize-handle/table-column-resize-handle.component';
import { CheckboxComponent } from '../../atoms';

@Component({
  selector: 'ocp-table-header',
  imports: [
    TranslatePipe,
    SortableHeaderComponent,
    CheckboxComponent,
    TableColumnResizeHandleComponent,
  ],
  template: `
    <tr class="table-header" [class.table-header--padded]="padCells()">
      @for (col of columns(); track col.field ?? $index) {
        @switch (col.type) {
          @case ('checkbox') {
            <th scope="col" class="table-header__checkbox-col" [attr.data-field]="col.field">
              <ocp-checkbox
                [checked]="tableData.allSelected()"
                [indeterminate]="tableData.someSelected()"
                (changeEvent)="tableData.toggleSelectAll()"
                [label]="col.label ? (col.label | translate) : ''"
              >
                @if (tableData.someSelected()) {
                  <ng-template #checkboxicon>
                    <span [style.color]="'white'">-</span>
                  </ng-template>
                }
              </ocp-checkbox>
            </th>
          }
          @case ('sortable') {
            <th
              scope="col"
              class="table-header__col"
              [class.table-header__col--resizable]="col.resizable"
              [attr.data-field]="col.field"
              [style.width]="getColumnWidth(col)"
              [style.min-width]="col.minWidth"
            >
              <ocp-sortable-header
                [label]="col.label!"
                [active]="tableData.sortField() === col.field"
                [direction]="tableData.sortDirection()"
                [truncate]="truncateSortableLabels()"
                (sort)="tableData.sort(col.field!)"
              />
              <ocp-table-column-resize-handle [column]="col" />
            </th>
          }
          @case ('text') {
            <th
              scope="col"
              class="table-header__col"
              [class.table-header__col--resizable]="col.resizable"
              [attr.data-field]="col.field"
              [style.width]="getColumnWidth(col)"
              [style.min-width]="col.minWidth"
            >
              <span
                class="table-header__label"
                [class.table-header__label--truncate]="truncateSortableLabels()"
              >
                {{ col.label! | translate }}
              </span>
              <ocp-table-column-resize-handle [column]="col" />
            </th>
          }
          @case ('actions') {
            <th
              scope="col"
              class="table-header__col table-header__col--actions"
              [class.table-header__col--resizable]="col.resizable"
              [class.table-header__col--sticky]="col.sticky"
              [attr.data-field]="col.field"
              [style.width]="getColumnWidth(col)"
              [style.min-width]="col.minWidth"
            >
              <span class="visually-hidden">{{ col.label! | translate }}</span>
              <ocp-table-column-resize-handle [column]="col" />
            </th>
          }
        }
      }
    </tr>
  `,
  styleUrl: './table-header.component.scss',
})
export class TableHeaderComponent {
  protected readonly tableData: OcpTableData<unknown> = inject(OcpTableData);

  readonly columns = input.required<TableColumnConfig[]>();
  readonly truncateSortableLabels = input(true);
  readonly padCells = input(false);

  protected readonly checkboxChecked = computed(
    () => this.tableData.someSelected() || this.tableData.allSelected()
  );
  readonly checkboxPt = input<object | undefined>({
    box: {
      style: {
        'border-color': 'var(--corporate-checkbox-border-color)',
        background: 'var(--corporate-checkbox-checked-background)',
      },
    },
    icon: {
      style: {
        color: 'var(--corporate-checkbox-icon-checked-color)',
      },
    },
  });

  /**
   * Sum of every column's frozen pixel width, once ALL columns (with a `field`) have one. Bind
   * this directly on the consumer's `<table>` so its width is pinned explicitly instead of via
   * CSS `width: 100%`/`max-content`, either of which cause `table-layout: fixed` to redistribute
   * width across every column whenever a single column is resized. Returns `null` before the
   * freeze completes, letting the default (hlmTable) CSS width apply for that first frame.
   *
   * Guards against reading `columns()` before it's set: consumers often bind this on an
   * ancestor `<table>` element, which Angular evaluates before this component's own `[columns]`
   * input binding runs on its first change-detection pass.
   */
  readonly tableWidthPx = computed<number | null>(() => {
    let columns: TableColumnConfig[];
    try {
      columns = this.columns();
    } catch {
      return null;
    }

    const sizing = this.tableData.columnSizing();
    const fields = columns.map(col => col.field).filter((field): field is string => !!field);

    if (fields.length === 0 || fields.some(field => sizing[field] === undefined)) {
      return null;
    }

    return fields.reduce((total, field) => total + sizing[field], 0);
  });

  protected getColumnWidth(col: TableColumnConfig): string | undefined {
    return getResolvedColumnWidth(
      col,
      this.tableData.columnSizing(),
      this.tableData.columnWidthPercentages()
    );
  }
}
